import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, MessageSquare, Phone, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CharCounter } from '@/components/ui/char-counter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import conversationService, { type ConversationListItem } from '@/services/conversationService';
import authService from '@/services/authService';
import { useAgentNumbers } from '@/components/agents/AgentNumberManager';
import supportTicketService, {
  SUPPORT_TICKET_CHANNEL_LABELS,
  type SupportTicketChannel,
  type SupportTicketConfig,
  type SupportTicketDetail,
} from '@/services/supportTicketService';
import {
  TICKET_FIELD_ORDER,
  emptyDraft,
  toCreatePayload,
  validateTicketDraft,
  type TicketDraft,
  type TicketErrors,
  type TicketField,
} from './ticketDraft';
import { ChoiceGroup } from './ChoiceGroup';
import { formatEur } from './format';

const ALL_NUMBERS = '__all__';
const NO_CONVERSATION = '__none__';
const RECENT_CONVERSATIONS = 30;

const CHANNEL_ICONS: Record<SupportTicketChannel, React.ElementType> = { voice: Phone, whatsapp: MessageSquare };

/** DOM-id je Feld — Ziel für Fokus und aria-describedby. */
const fieldId = (field: TicketField) => `ticket-${field}`;
const errorId = (field: TicketField) => `ticket-${field}-error`;

/** Anzeige-Nummer: nur echte Ziffernfolgen bekommen ein „+“ (Testläufe tragen z. B. „unknown“). */
function displayCaller(phone: string | null | undefined): string {
  const value = (phone || '').trim();
  return /^\d{5,}$/.test(value) ? `+${value}` : 'Unbekannte Nummer';
}

function conversationLabel(item: Pick<ConversationListItem, 'customer_name' | 'customer_phone' | 'last_message_at' | 'is_test'>) {
  const who = item.customer_name?.trim() || displayCaller(item.customer_phone);
  const when = item.last_message_at
    ? new Date(item.last_message_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
    : '';
  return `${who}${when ? ` · ${when}` : ''}${item.is_test ? ' · Test' : ''}`;
}

function Required() {
  return (
    <span className="text-destructive-strong" aria-hidden="true">
      *
    </span>
  );
}

function FieldError({ field, errors }: { field: TicketField; errors: TicketErrors }) {
  const message = errors[field];
  if (!message) return null;
  return (
    <p id={errorId(field)} className="text-xs text-destructive-strong">
      {message}
    </p>
  );
}

/** aria-Verknüpfung eines Eingabefelds mit seinem Fehlertext. */
function a11y(field: TicketField, errors: TicketErrors) {
  const invalid = Boolean(errors[field]);
  return {
    'aria-invalid': invalid || undefined,
    'aria-describedby': invalid ? errorId(field) : undefined,
  };
}

/** Fokus aufs erste fehlerhafte Feld; bei Radiogruppen auf deren Tab-Stopp. */
function focusFirstError(errors: TicketErrors) {
  const first = TICKET_FIELD_ORDER.find((field) => errors[field]);
  if (!first) return;
  const el = document.getElementById(fieldId(first));
  if (!el) return;
  const target =
    el.getAttribute('role') === 'radiogroup' ? el.querySelector<HTMLElement>('[tabindex="0"]') : el;
  target?.focus();
  target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

/**
 * Neues Support-Ticket. Die Felder sind bewusst Pflicht und mit Mindestlängen
 * versehen: ohne sie kommt „geht nicht" an, und wir müssten zurückfragen —
 * das kostet den Salon Zeit und uns Aufwand, der in der Pauschale steckt.
 */
export function SupportTicketForm({
  config,
  initialConversationId,
  onCancel,
  onCreated,
}: {
  config: SupportTicketConfig;
  initialConversationId: number | null;
  onCancel: () => void;
  onCreated: (ticket: SupportTicketDetail) => void;
}) {
  const { numbers } = useAgentNumbers();
  const limits = config.limits;
  const [draft, setDraft] = useState<TicketDraft>(emptyDraft);
  const [errors, setErrors] = useState<TicketErrors>({});
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [preselected, setPreselected] = useState<ConversationListItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Gespräch aus „Problem melden“ wird geladen — bis dahin keine Auswahl,
  // die das Laden anschließend überschreiben würde.
  const [preselecting, setPreselecting] = useState(Boolean(initialConversationId));
  const headingRef = useRef<HTMLDivElement>(null);

  // Ansicht gewechselt: Fokus auf die Überschrift, nicht zurück auf <body>.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const update = <K extends keyof TicketDraft>(key: K, value: TicketDraft[K], field?: TicketField) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    if (field) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Ansprechpartner und E-Mail aus dem Profil vorbelegen. Die Rückrufnummer
  // bewusst NICHT: die Salonnummer ist oft genau die, die beim Agenten landet.
  useEffect(() => {
    let cancelled = false;
    authService
      .getBusinessProfile()
      .then((profile: { contact_person?: string | null; email?: string | null }) => {
        if (cancelled) return;
        setDraft((prev) => ({
          ...prev,
          contactName: prev.contactName || profile.contact_person || '',
          contactEmail: prev.contactEmail || profile.email || '',
        }));
      })
      .catch(() => {
        // Nur die Vorbelegung fehlt.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Aus dem Verlauf heraus gemeldet: Gespräch und Agent übernehmen.
  useEffect(() => {
    if (!initialConversationId) return;
    let cancelled = false;
    setPreselecting(true);
    conversationService
      .getDetail(initialConversationId)
      .then((detail) => {
        if (cancelled) return;
        const channel: SupportTicketChannel = detail.record.type === 'phone' ? 'voice' : 'whatsapp';
        setPreselected({
          id: detail.record.id,
          type: detail.record.type,
          customer_phone: detail.customer_phone,
          customer_name: detail.customer_name,
          message_count: 0,
          last_message_at: detail.record.started_at || detail.record.last_message_at,
          status: detail.record.status,
          preview: null,
          is_test: detail.record.is_test,
        });
        setDraft((prev) => ({ ...prev, channel, conversationId: detail.record.id, noConversation: false }));
      })
      .catch(() => {
        if (!cancelled) toast.error('Das Gespräch konnte nicht geladen werden – bitte unten auswählen.');
      })
      .finally(() => {
        if (!cancelled) setPreselecting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initialConversationId]);

  // Letzte Gespräche des gewählten Agenten als Auswahl.
  useEffect(() => {
    if (!draft.channel) return;
    let cancelled = false;
    setConversationsLoading(true);
    conversationService
      .list(draft.channel === 'voice' ? 'phone' : 'whatsapp', undefined, RECENT_CONVERSATIONS)
      .then((res) => {
        if (!cancelled) setConversations(res.items);
      })
      .catch(() => {
        if (!cancelled) setConversations([]);
      })
      .finally(() => {
        if (!cancelled) setConversationsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [draft.channel]);

  const availableChannels = useMemo<SupportTicketChannel[]>(() => {
    if (numbers.length === 0) return ['voice', 'whatsapp'];
    const channels: SupportTicketChannel[] = [];
    if (numbers.some((n) => n.channel === 'voice' || n.channel === 'both')) channels.push('voice');
    if (numbers.some((n) => n.channel === 'whatsapp' || n.channel === 'both')) channels.push('whatsapp');
    return channels.length ? channels : ['voice', 'whatsapp'];
  }, [numbers]);

  const channelNumbers = useMemo(
    () => (draft.channel ? numbers.filter((n) => n.channel === 'both' || n.channel === draft.channel) : []),
    [numbers, draft.channel],
  );

  const conversationOptions = useMemo(() => {
    const expectedType = draft.channel === 'voice' ? 'phone' : 'whatsapp';
    const list = conversations.filter((c) => c.type === expectedType);
    if (preselected && preselected.type === expectedType && !list.some((c) => c.id === preselected.id)) {
      return [preselected, ...list];
    }
    return list;
  }, [conversations, preselected, draft.channel]);

  const selectChannel = (value: string) => {
    const channel = value as SupportTicketChannel;
    setDraft((prev) =>
      prev.channel === channel ? prev : { ...prev, channel, agentNumber: '', conversationId: null, noConversation: false },
    );
    setErrors((prev) => ({ ...prev, channel: undefined, conversation: undefined }));
  };

  const conversationValue =
    draft.conversationId !== null ? String(draft.conversationId) : draft.noConversation ? NO_CONVERSATION : '';

  const selectConversation = (value: string) => {
    if (value === NO_CONVERSATION) {
      setDraft((prev) => ({ ...prev, conversationId: null, noConversation: true }));
    } else {
      setDraft((prev) => ({ ...prev, conversationId: Number(value), noConversation: false, occurredAt: '' }));
    }
    setErrors((prev) => ({ ...prev, conversation: undefined, occurredAt: undefined }));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    const found = validateTicketDraft(draft, limits);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast.error('Bitte ergänze die markierten Angaben.');
      // Nach dem Rendern der Fehlertexte, damit aria-describedby schon zeigt.
      requestAnimationFrame(() => focusFirstError(found));
      return;
    }
    setSubmitting(true);
    try {
      const ticket = await supportTicketService.create(toCreatePayload(draft, config.price_eur));
      toast.success(`Ticket #${ticket.id} ist eingegangen – wir melden uns.`);
      onCreated(ticket);
    } catch (e: unknown) {
      toast.error(e instanceof Error && e.message ? e.message : 'Das Ticket konnte nicht eingereicht werden.');
    } finally {
      setSubmitting(false);
    }
  };

  const nowLocal = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const invalidInput = (field: TicketField) =>
    errors[field] ? 'border-destructive focus-visible:ring-destructive' : undefined;

  return (
    <Card className="shadow-sm border-border">
      <CardHeader>
        <button
          type="button"
          onClick={onCancel}
          className="mb-2 inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Zurück zu deinen Tickets
        </button>
        <CardTitle ref={headingRef} role="heading" aria-level={2} tabIndex={-1} className="text-lg outline-none">
          Neues Support-Ticket
        </CardTitle>
        <CardDescription>
          Je genauer die Angaben, desto schneller finden wir die Ursache. Alle mit <Required /> markierten Felder sind
          Pflicht.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-7" noValidate aria-busy={submitting}>
          {/* 1. Agent */}
          <section className="space-y-3">
            <p className="text-sm font-semibold">
              Welcher Agent ist betroffen? <Required />
            </p>
            <ChoiceGroup
              id={fieldId('channel')}
              label="Betroffener Agent"
              variant="card"
              options={availableChannels.map((channel) => ({
                value: channel,
                label: SUPPORT_TICKET_CHANNEL_LABELS[channel],
                icon: CHANNEL_ICONS[channel],
              }))}
              value={draft.channel}
              onChange={selectChannel}
              disabled={submitting || preselecting}
              invalid={Boolean(errors.channel)}
              errorId={errorId('channel')}
            />
            {preselecting && (
              <p role="status" className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                Gespräch aus dem Verlauf wird übernommen…
              </p>
            )}
            <FieldError field="channel" errors={errors} />
            {channelNumbers.length > 1 && (
              <div className="space-y-1.5">
                <Label htmlFor="ticket-agent-number">Unter welcher Nummer?</Label>
                <Select
                  value={draft.agentNumber || ALL_NUMBERS}
                  onValueChange={(v) => update('agentNumber', v === ALL_NUMBERS ? '' : v)}
                  disabled={submitting}
                >
                  <SelectTrigger id="ticket-agent-number" className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_NUMBERS}>Weiß ich nicht / alle Nummern</SelectItem>
                    {channelNumbers.map((n) => (
                      <SelectItem key={n.phone_number} value={n.phone_number}>
                        {n.display_name ? `${n.display_name} · ${n.phone_number}` : n.phone_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </section>

          {/* 2. Gespräch */}
          {draft.channel && (
            <section className="space-y-3">
              <Label htmlFor={fieldId('conversation')} className="text-sm font-semibold">
                Um welches Gespräch geht es? <Required />
              </Label>
              <p className="text-xs text-muted-foreground">
                Mit dem Verlauf sehen wir genau, was der Agent gesagt hat – das spart Rückfragen.
              </p>
              <Select value={conversationValue} onValueChange={selectConversation} disabled={submitting}>
                <SelectTrigger
                  id={fieldId('conversation')}
                  className={cn('bg-background', errors.conversation && 'border-destructive')}
                  {...a11y('conversation', errors)}
                >
                  <SelectValue placeholder={conversationsLoading ? 'Gespräche werden geladen…' : 'Gespräch auswählen'} />
                </SelectTrigger>
                <SelectContent>
                  {conversationOptions.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {conversationLabel(c)}
                    </SelectItem>
                  ))}
                  <SelectItem value={NO_CONVERSATION}>Kein Verlauf vorhanden / nicht dabei</SelectItem>
                </SelectContent>
              </Select>
              <FieldError field="conversation" errors={errors} />
              {draft.noConversation && (
                <div className="space-y-1.5">
                  <Label htmlFor={fieldId('occurredAt')}>
                    Wann ist es passiert? <Required />
                  </Label>
                  <Input
                    id={fieldId('occurredAt')}
                    type="datetime-local"
                    max={nowLocal}
                    value={draft.occurredAt}
                    onChange={(e) => update('occurredAt', e.target.value, 'occurredAt')}
                    disabled={submitting}
                    className={cn('max-w-xs', invalidInput('occurredAt'))}
                    {...a11y('occurredAt', errors)}
                  />
                  <FieldError field="occurredAt" errors={errors} />
                </div>
              )}
            </section>
          )}

          {/* 3. Kategorie */}
          <section className="space-y-3">
            <p className="text-sm font-semibold">
              Was ist schiefgelaufen? <Required />
            </p>
            <ChoiceGroup
              id={fieldId('category')}
              label="Art des Problems"
              options={config.categories.map((c) => ({ value: c.code, label: c.label }))}
              value={draft.category}
              onChange={(code) => update('category', code, 'category')}
              disabled={submitting}
              invalid={Boolean(errors.category)}
              errorId={errorId('category')}
            />
            <FieldError field="category" errors={errors} />
          </section>

          {/* 4. + 5. Beschreibung */}
          <section className="space-y-1.5">
            <Label htmlFor={fieldId('description')} className="text-sm font-semibold">
              Was ist passiert? <Required />
            </Label>
            <Textarea
              id={fieldId('description')}
              value={draft.description}
              onChange={(e) => update('description', e.target.value, 'description')}
              maxLength={limits.description_max}
              rows={5}
              disabled={submitting}
              placeholder="z. B. „Die Kundin wollte Strähnen buchen. Der Agent hat stattdessen einen Herrenhaarschnitt eingetragen und die Leistung vorher nicht vorgelesen.“"
              className={invalidInput('description')}
              {...a11y('description', errors)}
            />
            <CharCounter
              value={draft.description}
              max={limits.description_max}
              hint="Was hat der Agent gesagt oder getan? Welche Leistung, welcher Termin, welche Antwort?"
            />
            <FieldError field="description" errors={errors} />
          </section>

          <section className="space-y-1.5">
            <Label htmlFor={fieldId('expected')} className="text-sm font-semibold">
              Was hätte der Agent stattdessen tun sollen? <Required />
            </Label>
            <Textarea
              id={fieldId('expected')}
              value={draft.expected}
              onChange={(e) => update('expected', e.target.value, 'expected')}
              maxLength={limits.expected_max}
              rows={3}
              disabled={submitting}
              placeholder="z. B. „Strähnen buchen und die gewählte Leistung vor dem Buchen noch einmal bestätigen lassen.“"
              className={invalidInput('expected')}
              {...a11y('expected', errors)}
            />
            <CharCounter value={draft.expected} max={limits.expected_max} />
            <FieldError field="expected" errors={errors} />
          </section>

          {/* 6. Häufigkeit */}
          <section className="space-y-3">
            <p className="text-sm font-semibold">
              Wie oft passiert das? <Required />
            </p>
            <ChoiceGroup
              id={fieldId('frequency')}
              label="Häufigkeit"
              options={config.frequencies.map((f) => ({ value: f.code, label: f.label }))}
              value={draft.frequency}
              onChange={(code) => update('frequency', code, 'frequency')}
              disabled={submitting}
              invalid={Boolean(errors.frequency)}
              errorId={errorId('frequency')}
            />
            <FieldError field="frequency" errors={errors} />
          </section>

          {/* 7. Kontakt */}
          <section className="space-y-3">
            <p className="text-sm font-semibold">Wen erreichen wir bei Rückfragen?</p>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor={fieldId('contactName')}>
                  Ansprechpartner <Required />
                </Label>
                <Input
                  id={fieldId('contactName')}
                  value={draft.contactName}
                  onChange={(e) => update('contactName', e.target.value, 'contactName')}
                  maxLength={limits.contact_name_max}
                  disabled={submitting}
                  autoComplete="name"
                  className={invalidInput('contactName')}
                  {...a11y('contactName', errors)}
                />
                <FieldError field="contactName" errors={errors} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={fieldId('contactPhone')}>
                  Rückrufnummer <Required />
                </Label>
                <Input
                  id={fieldId('contactPhone')}
                  type="tel"
                  value={draft.contactPhone}
                  onChange={(e) => update('contactPhone', e.target.value, 'contactPhone')}
                  maxLength={limits.contact_phone_max}
                  disabled={submitting}
                  autoComplete="tel"
                  placeholder="Nicht die Nummer des Agenten"
                  className={invalidInput('contactPhone')}
                  {...a11y('contactPhone', errors)}
                />
                <FieldError field="contactPhone" errors={errors} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={fieldId('contactEmail')}>E-Mail für Antworten</Label>
                <Input
                  id={fieldId('contactEmail')}
                  type="email"
                  value={draft.contactEmail}
                  onChange={(e) => update('contactEmail', e.target.value, 'contactEmail')}
                  maxLength={limits.contact_email_max}
                  disabled={submitting}
                  autoComplete="email"
                  className={invalidInput('contactEmail')}
                  {...a11y('contactEmail', errors)}
                />
                <FieldError field="contactEmail" errors={errors} />
              </div>
            </div>
          </section>

          {/* Preis */}
          <section className="space-y-2">
            <div
              className={cn(
                'flex items-start gap-3 rounded-lg border bg-muted p-4',
                errors.priceAccepted ? 'border-destructive' : 'border-border',
              )}
            >
              <Checkbox
                id={fieldId('priceAccepted')}
                checked={draft.priceAccepted}
                onCheckedChange={(checked) => update('priceAccepted', checked === true, 'priceAccepted')}
                disabled={submitting}
                className="mt-0.5"
                {...a11y('priceAccepted', errors)}
              />
              <Label htmlFor={fieldId('priceAccepted')} className="text-sm font-normal leading-relaxed">
                Ich beauftrage die Bearbeitung zum Pauschalpreis von{' '}
                <strong>{formatEur(config.price_eur)} netto</strong> (bis zu {config.included_hours} Stunden Aufwand).
                Berechnet wird erst, wenn das Problem gelöst ist. Liegt der Fehler bei uns, ist die Bearbeitung kostenlos.
              </Label>
            </div>
            <FieldError field="priceAccepted" errors={errors} />
          </section>

          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={submitting} className="bg-[#1967D2] hover:bg-[#1558b0]">
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {submitting ? 'Wird eingereicht…' : 'Ticket kostenpflichtig einreichen'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default SupportTicketForm;
